import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { COLUMNS } from "./columns";
import {
  useTable,
  useRowSelect,
  useExpanded,
  usePagination
} from "react-table";
import { noDataPresent, tooltiptextId } from "../../../../../../locales/en";
import {
  cleanQuoteId,
  formatDateWithDayjs,
  showToast
} from "../../../../../../utils/utils";
import {
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType
} from "../../../../../../utils/constants/appConstants";
import privateAxios from "../../../../../../utils/axiosPrivate";
import ExpandToggle from "../../../../../common/ExpandToggle";
import { Tooltip } from "react-tooltip";
import "./../../../reportable/reportable.scss";
import { getLTVReportsModalDetailsUrl } from "../../../../../../utils/constants/apiEndPoints";
import TableSkeleton from "../../../../../skeletons/TableSkeleton";
import ErrorBoundary from "../../../../../error-boundary";
import ListViewFallback from "../../../../../error-boundary/fallback/ListViewFallback";
import { INVOICE_DETAILS } from "../../../../../../route/paths";
import { Link } from "react-router-dom";
import DualPagination from "../../../../../common/DualPagination";

const DATE_COLUMNS = ["dateFunded", "fundedAt", "dueDate"];
const DATE_PAID_COLUMNS = ["datePaid"];

const LTVReportInvoiceTable = ({ invoiceDetails }) => {
  const columns = useMemo(() => COLUMNS, []);
  const [expandedRows, setExpandedRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },

    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns]);
    }
  );

  const { pageIndex } = state;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  useEffect(() => {
    if (!invoiceDetails || !invoiceDetails.length) {
      setData([]);
      setLoading(false);
      return;
    }

    const loadLTVModalData = async () => {
      setLoading(true);
      try {
        const payload = invoiceDetails;
        const response = await privateAxios.post(
          getLTVReportsModalDetailsUrl,
          payload,
          {
            params: {
              page: pageIndex,
              size: pageSize
            }
          }
        );

        const invoices = response?.data?.data?.invoices || [];
        setData(invoices);
        setTotalPages(response?.data?.pageInfo?.totalPages ?? 0);
      } catch (e) {
        showToast(e?.response?.data?.message, showToastType.error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadLTVModalData();
  }, [invoiceDetails, pageIndex, pageSize]);

  return (
    <div className="col-md-12">
      <ErrorBoundary fallback={<ListViewFallback />}>
        <table
          className={`mt-2 mb-3 reportable1 bgless_table`}
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} className="parentrow" {...rest}>
                  <th />
                  {headerGroup.headers.map((column) => (
                    <th key={column.id} {...column.getHeaderProps()}>
                      <span>{column.render("Header")}</span>
                    </th>
                  ))}
                </tr>
              );
            })}
          </thead>

          {loading ? (
            <tbody>
              <TableSkeleton rows={10} columns={12} />
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 2} className="text-center">
                    {noDataPresent}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  const isExpanded = expandedRows.includes(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <tr className="parentrow" {...row.getRowProps()}>
                        <td className="expand-td">
                          <ExpandToggle
                            isExpanded={isExpanded}
                            onToggle={() => toggleRow(row.id)}
                            id={tooltiptextId.ltvReportId}
                          />
                        </td>
                        {row.cells.map((cell) => {
                          let content = cell.render("Cell");

                          if (cell.column.id === "FTQuoteID") {
                            const cleanId = cleanQuoteId(
                              cell.row.original.FTQuoteID
                            );
                            content = (
                              <Link to={INVOICE_DETAILS(cleanId)}>
                                {cell.render("Cell")}
                              </Link>
                            );
                          } else if (DATE_COLUMNS.includes(cell.column.id)) {
                            content = cell.value
                              ? formatDateWithDayjs(cell.value, "DD-MMM-YYYY")
                              : "";
                          } else if (
                            DATE_PAID_COLUMNS.includes(cell.column.id)
                          ) {
                            content = cell.value
                              ? formatDateWithDayjs(
                                  cell.value,
                                  "DD-MMM-YYYY HH:mm:ss"
                                )
                              : "";
                          }
                          return (
                            <td
                              key={cell.getCellProps().key}
                              {...cell.getCellProps()}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && (
                        <tr className="childrow colexpand">
                          <td colSpan={columns.length + 2}>
                            <div className="tdbody">
                              {columns.map((column) => {
                                const value = row.original[column.accessor];
                                let content = value;

                                if (DATE_COLUMNS.includes(column.accessor)) {
                                  content = value
                                    ? formatDateWithDayjs(value, "DD-MMM-YYYY")
                                    : "";
                                }

                                if (
                                  DATE_PAID_COLUMNS.includes(column.accessor)
                                ) {
                                  content = value
                                    ? formatDateWithDayjs(
                                        value,
                                        "DD-MMM-YYYY HH:mm:ss"
                                      )
                                    : "";
                                }

                                return (
                                  <span
                                    key={column.accessor}
                                    data-label={column.Header}
                                  >
                                    {content}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          )}
        </table>
        {data && data?.length > 0 && (
          <DualPagination
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            pageCount={pageCount}
            pageOptions={pageOptions || []}
            setPageSize={setPageSize}
            pageSize={pageSize}
            dataLength={data.length}
          />
        )}
      </ErrorBoundary>
      <Tooltip id={tooltiptextId.ltvReportId} place="top" />
    </div>
  );
};

LTVReportInvoiceTable.propTypes = {
  invoiceDetails: PropTypes.array
};
export default LTVReportInvoiceTable;
